import { postRequest } from '@rideiq/api/utils/provider';
import { ApiResponse } from '@rideiq/api/utils/responseTransform';

import { ChatProvider } from './types';

interface ZendeskTokenResponse {
  chatToken: string;
}

async function getZendeskChatToken(): Promise<ApiResponse<ZendeskTokenResponse>> {
  return postRequest<ZendeskTokenResponse, unknown>('zendesk_chat_token', {}, undefined, true);
}

class ZendeskProvider implements ChatProvider {
  name = 'zendesk';
  private initialized = false;

  isAvailable(): boolean {
    return !!window.env?.RIDEIQ_ZENDESK_API_KEY;
  }

  async start(_email: string): Promise<void> {
    const apiKey = window.env?.RIDEIQ_ZENDESK_API_KEY;

    if (!apiKey || this.initialized) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');

      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${apiKey}`;
      script.async = true;

      script.onload = async () => {
        this.initialized = true;

        // Authenticate user with Zendesk
        await this.loginUser();

        // Set conversation metadata
        this.setConversationMetadata();

        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Zendesk widget script'));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * Sets conversation metadata for Zendesk tickets.
   * This adds context for agents about the chat source and user.
   */
  private setConversationMetadata(): void {
    if (!window.zE) {
      return;
    }

    window.zE('messenger:set', 'conversationTags', ['client_ride_iq']);
  }

  /**
   * Authenticates the user with Zendesk using a JWT from the backend.
   * Backend endpoint should return a Zendesk-compatible JWT signed with
   * your Zendesk Messaging signing key.
   */
  private async loginUser(): Promise<void> {
    if (!window.zE) {
      return;
    }

    try {
      const response = await getZendeskChatToken();

      if (response?.type === 'success' && response.data?.chatToken) {
        window.zE('messenger', 'loginUser', (callback: (token: string) => void) => {
          callback(response.data.chatToken);
        });
      }
    } catch {
      // User will be anonymous in Zendesk chat if authentication fails
    }
  }

  open(): void {
    // With Zendesk, users use the floating bubble directly
    if (window.zE) {
      window.zE('messenger', 'open');
    }
  }

  show(): void {
    if (window.zE) {
      window.zE('messenger', 'show');
    }
  }

  hide(): void {
    if (window.zE) {
      window.zE('messenger', 'hide');
    }
  }
}

export const zendeskProvider = new ZendeskProvider();
